// src/services/productViewService.ts
import { supabase } from './supabase';

export interface ProductViewStats {
    today: number;
    total: number;
    lastWeek: number[];
}

class ProductViewService {
    private readonly VIEW_KEY_PREFIX = 'product_view_';

    // Отследить просмотр товара
    async trackProductView(productId: number): Promise<void> {
        try {
            const today = new Date().toISOString().split('T')[0];
            const storageKey = `${this.VIEW_KEY_PREFIX}${productId}_${today}`;

            // Проверяем, не смотрел ли пользователь этот товар сегодня
            const hasViewedToday = localStorage.getItem(storageKey);
            if (hasViewedToday) {
                return;
            }

            // ✅ ИСПРАВЛЕНО: используем maybeSingle вместо single
            const { data: existing, error: selectError } = await supabase
                .from('product_views')
                .select('id, view_count')
                .eq('product_id', productId)
                .eq('view_date', today)
                .maybeSingle(); // ← вместо .single()

            if (selectError) {
                console.error('Error checking existing view:', selectError);
                return;
            }

            if (existing) {
                // Обновляем существующую запись
                const { error: updateError } = await supabase
                    .from('product_views')
                    .update({ view_count: existing.view_count + 1 })
                    .eq('id', existing.id);

                if (updateError) {
                    console.error('Error updating view count:', updateError);
                }
            } else {
                // Создаем новую запись
                const { error: insertError } = await supabase
                    .from('product_views')
                    .insert([{
                        product_id: productId,
                        view_date: today,
                        view_count: 1
                    }]);

                if (insertError) {
                    console.error('Error inserting view count:', insertError);
                }
            }

            // Сохраняем в localStorage, чтобы сегодня больше не учитывать
            localStorage.setItem(storageKey, today);

        } catch (error) {
            console.error('Error tracking product view:', error);
        }
    }

    // Получить статистику просмотров для товара
    async getProductViewStats(productId: number): Promise<ProductViewStats> {
        try {
            const today = new Date().toISOString().split('T')[0];

            // ✅ ИСПРАВЛЕНО: используем maybeSingle
            const { data: todayData, error: todayError } = await supabase
                .from('product_views')
                .select('view_count')
                .eq('product_id', productId)
                .eq('view_date', today)
                .maybeSingle();

            if (todayError) {
                console.error('Error getting today views:', todayError);
            }

            // Получаем общее количество просмотров
            const { data: totalData, error: totalError } = await supabase
                .from('product_views')
                .select('view_count')
                .eq('product_id', productId);

            if (totalError) {
                console.error('Error getting total views:', totalError);
            }

            const total = totalData?.reduce((sum, item) => sum + item.view_count, 0) || 0;

            // Получаем просмотры за последние 7 дней
            const lastWeekData = await this.getLastWeekViews(productId);

            return {
                today: todayData?.view_count || 0,
                total: total,
                lastWeek: lastWeekData
            };
        } catch (error) {
            console.error('Error getting product view stats:', error);
            return { today: 0, total: 0, lastWeek: [] };
        }
    }

    // Получить просмотры за последние 7 дней
    private async getLastWeekViews(productId: number): Promise<number[]> {
        try {
            const lastWeek = new Date();
            lastWeek.setDate(lastWeek.getDate() - 7);
            const lastWeekStr = lastWeek.toISOString().split('T')[0];

            const { data, error } = await supabase
                .from('product_views')
                .select('view_count, view_date')
                .eq('product_id', productId)
                .gte('view_date', lastWeekStr)
                .order('view_date', { ascending: true });

            if (error) {
                console.error('Error getting last week views:', error);
                return [0, 0, 0, 0, 0, 0, 0];
            }

            // Создаем массив из 7 дней (заполняем нулями где нет данных)
            const result: number[] = [];
            for (let i = 6; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                const found = data?.find(d => d.view_date === dateStr);
                result.push(found?.view_count || 0);
            }

            return result;
        } catch (error) {
            console.error('Error in getLastWeekViews:', error);
            return [0, 0, 0, 0, 0, 0, 0];
        }
    }

    // Получить популярные товары (топ N)
    async getPopularProducts(limit: number = 10): Promise<{ product_id: number; total_views: number }[]> {
        try {
            const { data, error } = await supabase
                .from('product_views')
                .select('product_id, view_count')
                .order('view_count', { ascending: false });

            if (error) throw error;

            // Агрегируем просмотры по товарам
            const viewsMap = new Map<number, number>();
            data?.forEach(item => {
                const current = viewsMap.get(item.product_id) || 0;
                viewsMap.set(item.product_id, current + item.view_count);
            });

            // Сортируем и берем топ N
            const sorted = Array.from(viewsMap.entries())
                .map(([product_id, total_views]) => ({ product_id, total_views }))
                .sort((a, b) => b.total_views - a.total_views)
                .slice(0, limit);

            return sorted;
        } catch (error) {
            console.error('Error getting popular products:', error);
            return [];
        }
    }
}

export const productViewService = new ProductViewService();